/**
 * GT-ADMIN-MONITORING-ALERTS-READONLY-MR-03-ADMIN-UI-01 — pure UI
 * helpers shared across the monitoring panels.
 *
 * No imports from `services/**`, no fixtures, no DOM. Maps closed
 * backend enums onto design-system pill tones.
 */

import type { StatePillTone } from "@genius-tracker/design-system";
import { activeLocale, type Locale } from "@genius-tracker/i18n";
import type {
  MonitoringAlertState,
  MonitoringRuleHealth,
  MonitoringRuleState,
  MonitoringSeverity,
  MonitoringTargetHealth,
  MonitoringTargetLastErrorClass,
  MonitoringTextfileFreshness,
  VpsState,
} from "./types.js";

export const SECTION_HEADING_STYLE = {
  fontSize: "var(--gt-sys-font-size-lg)",
  fontWeight: "var(--gt-sys-font-weight-semibold)",
  margin: 0,
} as const;

export const SECTION_SUBTITLE_STYLE = {
  color: "var(--gt-sys-text-muted)",
  fontSize: "var(--gt-sys-font-size-sm)",
  margin: 0,
} as const;

export function severityTone(s: MonitoringSeverity): StatePillTone {
  switch (s) {
    case "critical":
      return "danger";
    case "warning":
      return "warn";
    case "info":
      return "info";
    case "unknown":
      return "neutral";
  }
}

export function alertStateTone(s: MonitoringAlertState): StatePillTone {
  switch (s) {
    case "firing":
      return "danger";
    case "pending":
      return "warn";
    case "inactive":
      return "neutral";
    case "resolved":
      return "success";
    case "unknown":
      return "neutral";
  }
}

export function ruleStateTone(s: MonitoringRuleState): StatePillTone {
  switch (s) {
    case "firing":
      return "danger";
    case "pending":
      return "warn";
    case "inactive":
      return "success";
    case "unknown":
      return "neutral";
  }
}

export function ruleHealthTone(h: MonitoringRuleHealth): StatePillTone {
  switch (h) {
    case "ok":
      return "success";
    case "err":
      return "danger";
    case "unknown":
      return "neutral";
  }
}

export function targetHealthTone(h: MonitoringTargetHealth): StatePillTone {
  switch (h) {
    case "up":
      return "success";
    case "down":
      return "danger";
    case "unknown":
      return "neutral";
  }
}

export function lastErrorClassTone(c: MonitoringTargetLastErrorClass): StatePillTone {
  switch (c) {
    case "none":
      return "success";
    case "timeout":
      return "warn";
    case "connection_refused":
      return "danger";
    case "http_error":
      return "warn";
    case "unknown":
      return "neutral";
  }
}

export function textfileFreshnessTone(f: MonitoringTextfileFreshness): StatePillTone {
  switch (f) {
    case "ok":
      return "success";
    case "stale":
      return "warn";
    case "missing":
      return "danger";
    case "unknown":
      return "neutral";
  }
}

/**
 * Render an ISO-8601 timestamp in the locale the app is rendering in, or
 * fall back to a clearly-non-data placeholder when the upstream value is
 * null / unparseable. Never throws.
 *
 * The locale is a parameter with a default rather than a hook, because the
 * callers pass this into table `cell` lambdas where a hook cannot run. Left
 * out, it resolves to the locale `LocaleProvider` is currently rendering.
 * The previous `toLocaleString()` followed the *runtime's* locale instead,
 * so an Admin switched to FR still read German dates on a German machine.
 */
export function formatIsoOrDash(
  value: string | null | undefined,
  locale: Locale = activeLocale(),
): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  // These are `toLocaleString()`'s own defaults, spelled out: same fields,
  // same four-digit year, only pinned to the app's locale, not the machine's.
  // `dateStyle`/`timeStyle` would look tidier and would shorten the year.
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  }).format(d);
}

/**
 * Bounded integer formatter — pads counters at 0 / 1 / 2 cleanly
 * and avoids Intl.NumberFormat's overhead for single-digit values.
 */
export function formatCount(n: number): string {
  if (!Number.isFinite(n)) return "0";
  return String(Math.max(0, Math.trunc(n)));
}

/* ── GT-INFRA-RESILIENCE-VPS-HEALTH — fleet formatting helpers ──── */

/** Fixed-decimal number in the given locale — the digit half of the two below. */
function decimals(value: number, places: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  }).format(value);
}

export function vpsStateTone(s: VpsState): StatePillTone {
  switch (s) {
    case "HEALTHY":
      return "success";
    case "WARNING":
      return "warn";
    case "CRITICAL":
    case "EMERGENCY":
      return "danger";
    case "MONITORING_BLIND":
      return "danger";
    case "UNKNOWN":
      return "neutral";
    default:
      return "neutral";
  }
}

/**
 * Bytes → human GiB/TiB string, or an em dash for null.
 *
 * Units and decimal places are unchanged; only the digits themselves now
 * follow the app's locale, so DE reads `2,5 GiB` where EN reads `2.5 GiB`.
 */
export function formatBytesOrDash(bytes: number | null, locale: Locale = activeLocale()): string {
  if (bytes === null || !Number.isFinite(bytes)) return "—";
  const gib = bytes / 1024 ** 3;
  if (gib >= 1024) return `${decimals(gib / 1024, 1, locale)} TiB`;
  if (gib >= 1) return `${decimals(gib, 1, locale)} GiB`;
  return `${decimals(bytes / 1024 ** 2, 0, locale)} MiB`;
}

/**
 * A percentage — `12.34` → `12.3%` — or an em dash.
 *
 * One decimal place as before. The percent sign is now placed by the
 * locale rather than glued on: FR writes `12,3 %` with its own spacing.
 */
export function formatPctOrDash(pct: number | null, locale: Locale = activeLocale()): string {
  if (pct === null || !Number.isFinite(pct)) return "—";
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(pct / 100);
}

/** Seconds → compact `Nd Nh` uptime, or an em dash. */
export function formatUptimeOrDash(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

export function formatDaysToFullOrDash(days: number | null): string {
  if (days === null || !Number.isFinite(days)) return "—";
  if (days >= 365) return "> 1y";
  return `${days.toFixed(days < 10 ? 1 : 0)}d`;
}
